import { PowerBIEmbed } from 'powerbi-client-react';
import { models, Report } from 'powerbi-client';
import './PairingDashboard.scss'
import {usePbi} from "../../providers/PowerBiProvider";
import {useTranslation} from "react-i18next";
import Loader from "../../components/Loader/Loader";
import {SearchBar} from "../../components/SearchBar/SearchBar";
import React, { useEffect, useState} from "react";
import {SearchStateInterface} from "../../models/SearchInterface";
import {useProfile} from "../../providers/Profile";
import {useCollectiveAgreement} from "../../providers/CollectiveAgreement";
import {generatePrevNMonths, getFormattedMonth} from "../../helpers/dateHelpers";
import {dates as months}  from '../../constants/dates';
import ReplayIcon from '@mui/icons-material/Replay';
import { useSearchOptions } from "../../providers/SearchOptions";
import { FreezePeriodInterface } from '../../models/FreezePeriodInterface';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import {pushSiteInfo, registerPageInfo} from '../../helpers/analyticsHelper';
import { useAuth } from '../../providers/Auth';

export default function PairingDashboard() {
    const PBIContext  = usePbi();
    const { t } = useTranslation();
    const profileContext = useProfile();
    const [searchVisibility, setSearchVisibility] = useState(false);
    const collectiveAgreementContext = useCollectiveAgreement();
    const freezePeriodContext = useFreezePeriod();
    const [dateOptions, setDateOptions] = useState<string[]>([]);
    const [currentPairingMonthData, setCurrentPairingMonthData] = useState<FreezePeriodInterface>();

    const concatDateName = () => {
        if(!currentPairingMonthData) return generatePrevNMonths(13, 3).map(f => `${f.month} ${f.year}`);
        return generatePrevNMonths(13, 2, new Date(currentPairingMonthData.pairingYear, currentPairingMonthData.pairingMonth - 1)).map(f => `${f.month} ${f.year}`);
    };

    const ALL = 'all';

    const [searchState, setSearchState] = useState<SearchStateInterface>({
        selectedDate: '',
        selectedBase: '',
        selectedEquipment: '',
        selectedPosition: ''
    });

    const [defaultSearchState, setDefaultSearchState] = useState<SearchStateInterface>({
        selectedDate: '',
        selectedBase: '',
        selectedEquipment: '',
        selectedPosition: ''
      });

    /* eslint-disable */
    const [reportState, setReportState] = useState<Report | null> ( null);

    const [baseFilter, setBaseFilter] = useState({
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
            table: "MAP",
            column: "BASE",
        },
        operator: "In",
        values:[searchState.selectedBase],
        filterType: models.FilterType.Basic,
        requireSingleSelection: false
    });

    const [positionFilter, setPositionFilter] = useState({
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
            table: "MAP",
            column: "Position",
        },
        operator: "In",
        values: [''],
        filterType: models.FilterType.Basic,
        requireSingleSelection: false
    });

    const [equipmentFilter, setEquipmentFilter] = useState ({
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
            table: "MAP",
            column: "Equipment",
        },
        operator: "In",
        values: [''],
        filterType: models.FilterType.Basic,
        requireSingleSelection: false
    });

    const [dateFilter, setDateFilter] = useState({
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
            table: "PAIRINGMONTH",
            column: "MONTHLABEL",
        },
        operator: "In",
        values: [''],
        filterType: models.FilterType.Basic,
        requireSingleSelection: false
    });

    const toggleNewSearch = () => {
        setSearchVisibility((prev) => !prev);
    };

    const {equipmentOptions, positionOptions, baseOptions} = useSearchOptions();

    useEffect(() => {
        // getting the access token
        PBIContext.getEmbedToken();

        if(searchState.selectedBase.toLowerCase() === ALL) {
            baseFilter.values = baseOptions
        } else {
            baseFilter.values = new Array(searchState.selectedBase)
        }

        if(searchState.selectedEquipment.toLowerCase() === ALL) {
            equipmentFilter.values = equipmentOptions
        } else {
            equipmentFilter.values = new Array(searchState.selectedEquipment)
        }

        if(searchState.selectedPosition.toLowerCase() === ALL) {
            positionFilter.values = positionOptions
        } else {
            positionFilter.values = new Array(searchState.selectedPosition)
        }

        //convert EX JAN-2021 format to 2021-01
        if(searchState.selectedDate !== '') {
            let date = searchState.selectedDate.split(' ');
            let monthToNum = String(months.indexOf(date[0]) + 1);
            if(monthToNum.length === 1) {
                monthToNum = '0'+monthToNum;
            }
            dateFilter.values = new Array(date[1] + '-' + monthToNum);
        }

        /* eslint-disable */
        if(reportState) {
            setBaseFilter(baseFilter)
            setPositionFilter(positionFilter)
            setEquipmentFilter(equipmentFilter)
            setDateFilter(dateFilter)
            reportState.reload();
        }

    }, [searchState])

    useEffect( () => {
        if( !freezePeriodContext.freezePeriodData.pairingFreeze || freezePeriodContext.loading ) {
          return;
        }
        setCurrentPairingMonthData({ ...freezePeriodContext.freezePeriodData.pairingFreeze.filter(val => val)[0] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [freezePeriodContext]);

    useEffect(() => {
        if (!profileContext.profile.results || !currentPairingMonthData) {
            return;
        }

        let selectedDate = getFormattedMonth(currentPairingMonthData.pairingMonth, currentPairingMonthData.pairingYear);
        if(currentPairingMonthData && currentPairingMonthData.dbmPosted === false) {
            selectedDate = `${generatePrevNMonths(13, 2, new Date(currentPairingMonthData.pairingYear, currentPairingMonthData.pairingMonth - 1))[1].month} ${generatePrevNMonths(13, 2, new Date(currentPairingMonthData.pairingYear, currentPairingMonthData.pairingMonth - 1))[1].year}`;
        }
        setSearchState({
            ...searchState,
            selectedDate: selectedDate,
            selectedBase: profileContext.profile.results?.baseLocation,
            selectedEquipment: profileContext.profile.results?.equipment,
            selectedPosition: profileContext.profile.results?.position
        });

        setDefaultSearchState({
            ...searchState,
            selectedDate: selectedDate,
            selectedBase: profileContext.profile.results?.baseLocation,
            selectedEquipment: profileContext.profile.results?.equipment,
            selectedPosition: profileContext.profile.results?.position
          });
          setDateOptions(concatDateName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[profileContext.profile.results, collectiveAgreementContext.collectiveAgreement, currentPairingMonthData, currentPairingMonthData])

    const user = useAuth();
    useEffect(() => {

        if (user.loading)
            return;

        if (!profileContext.profile.results) {
            return;
        }

        pushSiteInfo(user.user,profileContext.profile);

        registerPageInfo('my access app|planning & bidding|pairing dashboard', {
            "level1": "my access app",
            "level2": "planning & bidding",
            "level3": "pairing dashboard",
            "level4": null
        })

    }, [user, profileContext.profile.results]);

    return (
        profileContext.loading ? <Loader/> : (
        <div className='dashboard-content'>
            <div className="header-grey">
                <div className="header-container">
                    <h1>{t('pairingDashboard.title')}</h1>
                    {!searchVisibility ? (
                            <div>
                                <div className='sub-title'>
                                    <span>{searchState.selectedDate}</span>
                                    {searchState.selectedBase.toLowerCase() === 'all' ? <span>{t('SearchBar.allbases')}</span> : <span>{searchState.selectedBase}</span>}
                                    <span>{searchState.selectedEquipment}</span>
                                    {searchState.selectedPosition.toLowerCase() === 'all' ? <span>{t('SearchBar.allpos')}</span> : <span>{searchState.selectedPosition}</span>}
                                </div>
                                <span className='new-search-link link-states' onClick={toggleNewSearch}>
                                     <i className="icon-manage_accounts_FILL0_wght400_GRAD0_opsz48-1-1 change-position-icon" />
                                    {t('pairingPackage.newSearch')}
                                </span>
                            </div>
                        ):
                        <div className='search-bar-container'>
                            <SearchBar
                                dateOptions={dateOptions}
                                searchState={searchState}
                                setSearchVisibility={setSearchVisibility}
                                setSearchState={setSearchState}
                                includeDateField={true}
                                showAll={false}
                                defaultSearchState={defaultSearchState}
                                showCancel={true}
                                setSearchName={t('pairingDashboard.updateButton') ?? ''}
                            />
                        </div> }
                </div>
            </div>
            <div className='reset-button-container' onClick={() => window.location.reload()}>
                <span className='reset-button-link link-states' >Reset Dashboard</span>
                <ReplayIcon fontSize='large' className='replay-con link-states' />
            </div>
            <div>
                {
                    PBIContext.loading ? <Loader/> : (
                    <PowerBIEmbed
                        embedConfig = {{
                            type: 'report',
                            id: process.env.REACT_APP_POWER_BI_REPORT_ID,
                            accessToken: PBIContext.embedToken.token,
                            tokenType: models.TokenType.Embed,
                            settings: {
                                filterPaneEnabled: false,
                                background: models.BackgroundType.Transparent,
                            },
                            filters: [baseFilter, equipmentFilter, positionFilter, dateFilter]
                        }}
                        getEmbeddedComponent={(embeddedReport) => {
                            if(embeddedReport != null) {
                                setReportState(embeddedReport as Report)
                            }
                        }}
                        cssClassName = { "report-style-class" }
                    />
                )}
            </div>
        </div>)
    )
}
